/**
 * CreateKnowledgeBaseDialog - placeholder modal (no real submit yet).
 * Validates that a name is entered and at least one document is selected,
 * then reports that
 * creation is not implemented.
 * Opened from the "+ New Knowledge Base" row at the bottom of the KB section.
 */
import { useRef, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import FormHelperText from '@material-ui/core/FormHelperText';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadIcon from '@material-ui/icons/CloudUploadOutlined';
import { makeStyles } from '@material-ui/core/styles';
import { useDialogStyles } from '../../dialogs/dialogStyles';
import { useTranslation } from 'react-i18next';

type Props = { open: boolean; onClose: () => void };

const fileKey = (f: File) => `${f.name}-${f.size}-${f.lastModified}`;

const ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.md', '.xlsx'];

const isAccepted = (file: File) =>
  ACCEPTED_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext));

const useStyles = makeStyles(theme => ({
  dropzone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(4, 3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
  },
  dropzoneError: { borderColor: theme.palette.error.main },
  dropzoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
  fileList: {
    listStyle: 'none',
    margin: theme.spacing(1, 0, 0),
    padding: 0,
  },
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    fontSize: '0.85rem',
    color: theme.palette.text.primary,
  },
  fileName: { overflowWrap: 'anywhere' },
  dropzoneHint: { fontSize: '0.74rem', color: theme.palette.text.secondary },
}));

export function CreateKnowledgeBaseDialog({ open, onClose }: Props) {
  const c = useDialogStyles();
  const classes = useStyles();
  const { t } = useTranslation('knowledgeBase');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [showNameError, setShowNameError] = useState(false);
  const [showNoDocumentsError, setShowNoDocumentsError] = useState(false);
  const [showNotImplementedError, setShowNotImplementedError] = useState(false);
  const [rejectedNames, setRejectedNames] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    setName('');
    setDescription('');
    setFiles([]);
    setShowNameError(false);
    setShowNoDocumentsError(false);
    setShowNotImplementedError(false);
    setRejectedNames([]);
    setDragActive(false);
    onClose();
  };

  const selectFiles = (picked: File[]) => {
    const accepted = picked.filter(isAccepted);
    setRejectedNames(picked.filter(f => !isAccepted(f)).map(f => f.name));
    if (accepted.length > 0) {
      // Keep the files chosen earlier; skip ones that are already in the list.
      setFiles(prev => {
        const known = new Set(prev.map(fileKey));
        return [...prev, ...accepted.filter(f => !known.has(fileKey(f)))];
      });
      setShowNoDocumentsError(false);
      setShowNotImplementedError(false);
    }
  };

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    selectFiles(Array.from(e.target.files ?? []));
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    selectFiles(Array.from(e.dataTransfer.files));
  };

  const handleRemoveFile = (file: File) => {
    setFiles(prev => prev.filter(f => fileKey(f) !== fileKey(file)));
    setShowNotImplementedError(false);
  };

  const handleCreate = () => {
    const missingName = name.trim() === '';
    const missingDocuments = files.length === 0;
    setShowNameError(missingName);
    setShowNoDocumentsError(missingDocuments);
    setShowNotImplementedError(false);
    if (missingName || missingDocuments) {
      return;
    }
    // TODO(CB25SES-1798): call the create-knowledge-base API once the backend
    // provides it, then close the dialog and refresh the sidebar list.
    // The backend has no create-knowledge-base API yet, so tell the user
    // instead of closing the dialog as if the knowledge base was created.
    setShowNotImplementedError(true);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography className={c.title}>
        <span className={c.titleTextWrap}>
          <span className={c.titleText}>{t('dialogs.title')}</span>
        </span>
        <IconButton
          size="small"
          className={c.closeBtn}
          aria-label={t('dialogs.close')}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className={c.body}>
        <div className={c.field}>
          <label className={c.label} htmlFor="kb-name">
            {t('dialogs.name')}
          </label>
          <TextField
            id="kb-name"
            fullWidth
            size="small"
            variant="outlined"
            placeholder={t('dialogs.namePlaceholder')}
            value={name}
            error={showNameError}
            helperText={showNameError ? t('dialogs.nameError') : undefined}
            onChange={e => {
              setName(e.target.value);
              setShowNameError(false);
            }}
          />
        </div>

        <div className={c.field}>
          <label className={c.label} htmlFor="kb-desc">
            {t('dialogs.description')}
          </label>
          <TextField
            id="kb-desc"
            fullWidth
            multiline
            minRows={3}
            size="small"
            variant="outlined"
            placeholder={t('dialogs.descriptionPlaceholder')}
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        </div>

        <div className={c.field}>
          <div className={c.label}>{t('dialogs.documents')}</div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            data-testid="kb-file-input"
            accept={ACCEPTED_EXTENSIONS.join(',')}
            onChange={handleFilesSelected}
          />
          <div
            role="button"
            tabIndex={0}
            className={`${classes.dropzone} ${
              showNoDocumentsError ? classes.dropzoneError : ''
            } ${dragActive ? classes.dropzoneActive : ''}`}
            onDragOver={e => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
          >
            <CloudUploadIcon />
            {files.length === 0 && <div>{t('dialogs.noDocuments')}</div>}
            <div className={classes.dropzoneHint}>
              {t('dialogs.supportedFormats')}
            </div>
          </div>
          {files.length > 0 && (
            <ul className={classes.fileList}>
              {files.map(f => (
                <li key={fileKey(f)} className={classes.fileItem}>
                  <span className={classes.fileName}>{f.name}</span>
                  <IconButton
                    size="small"
                    aria-label={t('dialogs.removeFile', { name: f.name })}
                    onClick={() => handleRemoveFile(f)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </li>
              ))}
            </ul>
          )}
          {rejectedNames.length > 0 && (
            <FormHelperText error role="alert">
              {t('dialogs.unsupportedFiles', {
                names: rejectedNames.join(', '),
              })}
            </FormHelperText>
          )}
          {showNoDocumentsError && (
            <FormHelperText error role="alert">
              {t('dialogs.noDocumentsError')}
            </FormHelperText>
          )}
          {showNotImplementedError && (
            <FormHelperText error role="alert">
              {t('dialogs.createNotImplemented')}
            </FormHelperText>
          )}
        </div>
      </DialogContent>

      <DialogActions className={c.actions}>
        <Button onClick={handleClose}>{t('dialogs.cancel')}</Button>
        <Button variant="contained" color="primary" onClick={handleCreate}>
          {t('dialogs.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
